import type { Jsonifiable, Primitive } from "type-fest";

import { getLogger } from "@expert/logging";
import { useCallback, useEffect, useRef, useState } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";

import type { ExpertWorkspaceWebSocketMessagePayload, ExpertWorkspaceWebSocketMessageType, Tenant } from "../types";

import { expertWorkspaceWebSocketEventBus, WS_EMITTER_NAME } from "../eventBus";
import { parseSocketIOMessage, shouldHandleSocketIOMessage } from "../utils/socketIOMessageParser";

type NonPrimitiveJsonifiable = Exclude<Jsonifiable, Primitive>;

export type ExtraHeartbeatProps = NonPrimitiveJsonifiable;

export type UpsertExtraHeartbeatProps = (extraHeartbeatProps: ExtraHeartbeatProps) => void;

const heartbeatInterval = 60_000;

const logger = getLogger({
    module: "useWebSocketInternal",
});

export type Env = "production" | "development";

const tenantToClientMap: Record<Env, Record<Tenant, string>> = {
    development: {
        ExpertVoiceLink: "ExpertVoiceLinkNonProd",
        ExpertWorkspace: "ExpertWorkspaceDev",
    },
    production: {
        ExpertVoiceLink: "ExpertVoiceLinkProd",
        ExpertWorkspace: "ExpertWorkspaceProd",
    },
};

export type SendMessageToTenantType = <T>(
    tenant: Tenant,
    messageType: ExpertWorkspaceWebSocketMessageType,
    message: T,
) => void;

// NOTE: Code 1000 indicates the websocket closed normally.
// See documentation here https://developer.mozilla.org/en-US/docs/Web/API/CloseEvent/code
const NORMAL_WS_CLOSE_EVENT = 1000;
// 1002=Protocol Error,
// 1003=Unsupported Data,
// 1007=Invalid payload,
// 1008=Policy Violation,
// 1011=Internal Error,
// 1014=Bad Gateway,
// 1015=TLS Handshake Failure
const CRITICAL_ERROR_CODES = [1002, 1003, 1007, 1008, 1011, 1014, 1015];

export function useWebsocketInternal({
    token,
    identity,
    onDisconnect,
    environmentType,
    webSocketUrl,
    isWebsocketEnabled,
    useSocketIO = false,
}: {
    token: string;
    identity: string;
    onDisconnect?: () => void;
    environmentType: Env;
    webSocketUrl?: string;
    isWebsocketEnabled: boolean;
    useSocketIO?: boolean;
}) {
    const [loading, setLoading] = useState(true);
    const [failedToLoad, setFailedToLoad] = useState(false);
    const lastCloseEventRef = useRef<CloseEvent | null>(null);

    const [connected, setConnected] = useState(true);

    useEffect(() => {
        // Auto-reconnect upon disconnect
        if (!connected) {
            setConnected(true);
        }
    }, [connected]);

    const reconnect = useCallback(() => {
        logger.info("Manually triggering WebSocket reconnect");
        setConnected(false);
    }, []);

    const onReconnectStop = (attempts: number) => {
        logger.warn({ attempts }, "==== WEBSOCKET Reconnect stop event ====");
        setFailedToLoad(true);
    };

    const onClose = (event: CloseEvent) => {
        lastCloseEventRef.current = event;
        logger.info({ event }, "==== WEBSOCKET Close event ====");

        if (event.code === NORMAL_WS_CLOSE_EVENT && onDisconnect) {
            onDisconnect();
        }
    };

    const onError = (event: Event) => {
        const socket = getWebSocket();
        const lastClose = lastCloseEventRef.current;

        logger[CRITICAL_ERROR_CODES.includes(lastClose?.code ?? 0) ? "error" : "warn"](
            {
                eventType: event.type,
                isTrusted: event?.isTrusted,
                socketState: socket?.readyState, // 0 = CONNECTING, 1 = OPEN, 2 = CLOSING, 3 = CLOSED
                timeStamp: event.timeStamp,
                lastCloseEvent: lastClose
                    ? {
                          code: lastClose.code,
                          reason: lastClose.reason,
                          wasClean: lastClose.wasClean,
                          timeStamp: lastClose.timeStamp,
                      }
                    : undefined,
            },
            "==== WEBSOCKET Error event ====",
        );
    };

    const {
        sendJsonMessage: sendWebSocketMessage,
        readyState,
        getWebSocket,
    } = useWebSocket(
        webSocketUrl ?? null,
        {
            fromSocketIO: useSocketIO,
            queryParams: { token },
            heartbeat: {
                interval: heartbeatInterval,
                timeout: heartbeatInterval * 3,
                // override with socket.io message (2/3)
                message: () =>
                    JSON.stringify({
                        // oxlint-disable-next-line typescript/no-misused-spread
                        ...extraHeartbeatPropsRef.current,
                        action: "heartbeat",
                        correlationId: identity,
                    }),
            },
            shouldReconnect: (closeEvent) => closeEvent.code !== NORMAL_WS_CLOSE_EVENT,
            reconnectAttempts: 3,
            reconnectInterval: (attemptNumber) => Math.min(2 ** attemptNumber * 1000, 10000),
            onMessage: (message: MessageEvent) => onMessage(message, webSocketUrl, useSocketIO),
            onError,
            onClose,
            onOpen,
            onReconnectStop,
            skipAssert: import.meta.env.MODE === "test",
        },
        isWebsocketEnabled && connected,
    );

    useEffect(() => {
        setLoading(readyState !== ReadyState.OPEN);
    }, [loading, readyState]);

    const sendMessageToTenant: SendMessageToTenantType = useCallback(
        <T>(tenant: Tenant, messageType: ExpertWorkspaceWebSocketMessageType, message: T): void => {
            try {
                const mappedTenant = tenantToClientMap[environmentType][tenant];
                logger.info({ messageType, message, tenant }, "Sending message to tenant.");
                sendWebSocketMessage({
                    action: "sendMessageToTenant",
                    messageType,
                    tenant: mappedTenant,
                    payload: { ...message, sendTime: Date.now() },
                });
            } catch (error) {
                logger.error({ error }, "Failed to send WebSocket message to tenant.");
            }
        },
        [environmentType, sendWebSocketMessage],
    );

    // react-use-websocket heartbeat message option doesn't support passing in a new function as state changes
    // a ref is used so that props can be updated and changed from beat to beat
    const extraHeartbeatPropsRef = useRef<ExtraHeartbeatProps>({});

    const upsertExtraHeartbeatProps = useCallback<UpsertExtraHeartbeatProps>((extraHeartbeatProps) => {
        // oxlint-disable-next-line typescript/no-misused-spread
        extraHeartbeatPropsRef.current = { ...extraHeartbeatPropsRef.current, ...extraHeartbeatProps };
    }, []);

    const result = {
        sendWebSocketMessage,
        sendMessageToTenant,
        loading,
        readyState,
        failedToLoad,
        reconnect,
        upsertExtraHeartbeatProps,
    };
    return result;
}

const onMessage = (message: MessageEvent, webSocketUrl: string | undefined, isSocketIO: boolean = false) => {
    try {
        if (message.type !== "message") {
            logger.warn({ message }, `Unsupported message type: ${message.type}`);
            return;
        }
        if (!webSocketUrl) {
            logger.warn({ message }, "WebSocket URL is undefined");
            return;
        }
        if (message.origin !== new URL(webSocketUrl).origin) {
            logger.warn({ message }, `Message from unexpected origin: ${message.origin}`);
            return;
        }
        let event: ExpertWorkspaceWebSocketMessagePayload;
        try {
            if (isSocketIO) {
                if (shouldHandleSocketIOMessage(message)) {
                    event = parseSocketIOMessage(message);
                } else {
                    logger.info({ message }, "Skiping message that is not a socket.io data message");
                    return;
                }
            } else {
                event = JSON.parse(message.data as string) as ExpertWorkspaceWebSocketMessagePayload;
            }
        } catch (error: unknown) {
            logger.error({ error, message }, "Failed to parse message body");
            return;
        }

        logger.info({ event, protocol: isSocketIO ? "socket.io" : "websocket" }, "Received websocket message");
        expertWorkspaceWebSocketEventBus.emit(`${WS_EMITTER_NAME}_${event.messageType}`, event.payload);
    } catch (error) {
        logger.error({ error }, "Failed to handle onMessage for websocket");
    }
};

const onOpen = (event: Event) => {
    logger.info({ event }, "==== WEBSOCKET Open event ====");
};
